import React, { useState, useEffect, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import { subscribeToPlayerState } from '../../services/gameService';

// Lazy load games with corrected paths relative to src/components/Showdown
const AuraEater = React.lazy(() => import('../DreamWeave/games/aura-eater/AuraEater'));
const SynapticStacking = React.lazy(() => import('../DreamWeave/SynapticStacking'));
const SynapticSerpent = React.lazy(() => import('../DreamWeave/games/synaptic-serpent/SynapticSerpent'));
const CosmicAscent = React.lazy(() => import('../DreamWeave/CosmicAscent'));
const StarlightSentinel = React.lazy(() => import('../DreamWeave/games/starlight-sentinel/StarlightSentinel'));

const LiveGameViewer = ({ gameType, souls, syncId, remoteRole }) => {
    const [isConnected, setIsConnected] = useState(true);
    const [lastPacketTime, setLastPacketTime] = useState(Date.now());

    useEffect(() => {
        if (!syncId || !remoteRole) return;

        const unsubscribe = subscribeToPlayerState(syncId, remoteRole, (data) => {
            if (data) {
                setLastPacketTime(Date.now());
                setIsConnected(true);
            }
        });

        const checkConnection = setInterval(() => {
            if (Date.now() - lastPacketTime > 8000) { // 8 seconds timeout
                setIsConnected(false);
            }
        }, 2000);

        return () => {
            unsubscribe();
            clearInterval(checkConnection);
        };
    }, [syncId, remoteRole, lastPacketTime]);
    
    const renderGameComponent = () => {
        // If syncId is present, we are watching a remote player (Live Feed).
        // If syncId is missing, we are watching a local AI bot (Demo/Training).
        const isDemoMode = !syncId;

        const commonProps = {
            souls,
            isSpectatorMode: isDemoMode,
            isRemoteMode: !isDemoMode,
            syncId,
            remoteRole,
        };

        switch (gameType) {
            case 'auraEater':
                return <AuraEater {...commonProps} />;
            case 'starlightSentinel':
                return <StarlightSentinel {...commonProps} />;
            case 'synapticSerpent':
                return <SynapticSerpent {...commonProps} />;
            case 'synapticStacking':
                return <SynapticStacking {...commonProps} />;
            case 'cosmicAscent':
                return <CosmicAscent {...commonProps} />;
            default:
                return <div className="flex items-center justify-center h-full text-red-500">Unknown Game Feed</div>;
        }
    };

    return (
        <div className="relative w-full h-full bg-gray-900 rounded-xl overflow-hidden border border-gray-700 shadow-2xl flex flex-col">
            {/* Header / Overlay Info */}
            <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/80 to-transparent z-20 flex justify-between items-start pointer-events-none">
                <div>
                    <h3 className="text-xl font-bold text-white flex items-center gap-2 animate-pulse">
                        <LucideIcons.Radio className="text-red-500" />
                        LIVE FEED
                    </h3>
                    <p className="text-xs text-gray-400 uppercase tracking-widest">Receiving Transmission...</p>
                </div>
                <div className="flex flex-col items-end">
                    <div className="flex items-center gap-2 bg-black/50 px-3 py-1 rounded-full border border-gray-600">
                        <span className="text-xs text-gray-300">Signal Strength:</span>
                        <div className="flex gap-0.5">
                            <div className="w-1 h-3 bg-green-500 rounded-sm" />
                            <div className="w-1 h-3 bg-green-500 rounded-sm" />
                            <div className="w-1 h-3 bg-green-500 rounded-sm" />
                            <div className="w-1 h-3 bg-green-500/30 rounded-sm" />
                        </div>
                    </div>
                    <div className="mt-1 flex items-center gap-1">
                        <LucideIcons.Sparkles size={12} className="text-yellow-400" />
                        <span className="text-xs text-yellow-100">Power: {souls}</span>
                    </div>
                </div>
            </div>

            {/* Game Container */}
            <div className="flex-grow relative">
                {!isConnected && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                        <div className="text-center animate-pulse">
                            <LucideIcons.WifiOff size={48} className="mx-auto text-red-500 mb-2" />
                            <h3 className="text-xl font-bold text-white">Signal Lost</h3>
                            <p className="text-gray-400 text-sm">Opponent disconnected...</p>
                        </div>
                    </div>
                )}
                <Suspense fallback={<div className="w-full h-full flex items-center justify-center"><LoadingSpinner message="Establishing Link..." /></div>}>
                    {renderGameComponent()}
                </Suspense>
            </div>
        </div>
    );
};

export default LiveGameViewer;
